import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Building2, UserCog,
  ShieldCheck, Award, Scale, Lock, X, Briefcase, Inbox,
  Wallet, Banknote, Trophy, ClipboardCheck, Sun, Moon, MessageSquare, Sparkles, Crown, Kanban, Activity, Calculator, IdCard,
  PanelLeftClose, PanelLeftOpen, AlertTriangle,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { getRoleMeta, normalizeRole } from "@/components/RoleBadge";
import api from "@/lib/api";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-360-pro/artifacts/t5motboh_wertia%20perfect.png";

export default function Sidebar({ open = false, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const role = normalizeRole(user?.role);
  const meta = getRoleMeta(role);
  const [unreadChat, setUnreadChat] = useState(0);
  const [chatSenders, setChatSenders] = useState([]);
  const [sidebarBadges, setSidebarBadges] = useState({});
  // Desktop-only collapsed rail mode. Persisted across sessions.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("wertia_sidebar_collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const nv = !v;
      try { localStorage.setItem("wertia_sidebar_collapsed", nv ? "1" : "0"); } catch { /* */ }
      return nv;
    });
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/chat/unread-total");
        if (alive) {
          setUnreadChat(r.data?.total || 0);
          setChatSenders(r.data?.senders || []);
        }
      } catch { /* */ }
    };
    const fetchBadges = async () => {
      try {
        const r = await api.get("/sidebar-badges");
        if (alive) setSidebarBadges(r.data || {});
      } catch { /* */ }
    };
    fetchUnread();
    fetchBadges();
    const t = setInterval(() => { fetchUnread(); fetchBadges(); }, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  // Per-role menu — raggruppato per sezioni logiche per ridurre il rumore visivo.
  const sb = sidebarBadges || {};
  const baseItems = [
    { section: "TÄGLICH", roles: ["master", "control", "basic", "finanza"] },
    { to: "/dashboard", icon: LayoutDashboard, label: t.dashboard, testId: "nav-dashboard", roles: ["master", "control", "basic", "finanza"] },
    { to: "/tasks", icon: ClipboardCheck, label: "Aufgabe", testId: "nav-tasks", badge: sb.aufgabe || 0, roles: ["master", "control", "basic", "finanza"] },
    { to: "/chat", icon: MessageSquare, label: "Chat", testId: "nav-chat", badge: unreadChat, senders: unreadChat > 0 ? chatSenders : [], roles: ["master", "control", "basic", "finanza"] },
    { to: "/calendar", icon: CalendarDays, label: t.calendar, testId: "nav-calendar", badge: sb.kalender || 0, roles: ["master", "control", "basic"] },

    { section: "KUNDEN", roles: ["master", "control", "basic"] },
    { to: "/clients", icon: Users, label: t.clients, testId: "nav-clients", roles: ["master", "control", "basic"] },
    { to: "/genehmigtls", icon: Inbox, label: t.nav_genehmigtls, testId: "nav-genehmigtls", badge: sb.genehmigungen || 0, roles: ["master", "control"] },
    { to: "/antraege-board", icon: Kanban, label: "Anträge Board", testId: "nav-antraege-board", badge: sb.antraege_board || 0, roles: ["master", "control"] },

    { section: "PROVISIONEN", roles: ["master", "control", "basic", "finanza"] },
    { to: "/commissions", icon: Wallet, label: "Mein Cockpit", testId: "nav-commissions", roles: ["master", "control", "basic", "finanza"] },
    { to: "/buchhaltung", icon: Calculator, label: "Buchhaltung", testId: "nav-buchhaltung", roles: ["master"] },
    { to: "/konsulenten-anagrafica", icon: IdCard, label: "Konsulenten-Anagrafica", testId: "nav-anagrafica", roles: ["master"] },

    { section: "ANBIETER", roles: ["master", "control", "basic"] },
    { to: "/companies", icon: Building2, label: t.companies || "Gesellschaften", testId: "nav-companies", roles: ["master", "control", "basic"] },
    { to: "/anbieter-verwaltung", icon: Briefcase, label: "Anbieter-Verwaltung", testId: "nav-anbieter", badge: sb.anbieter || 0, roles: ["master"] },

    { section: "DIREKTION", roles: ["master", "finanza"] },
    { to: "/super-admin", icon: Crown, label: "Cockpit", testId: "nav-super-admin", badge: sb.cockpit || 0, roles: ["master"] },
    { to: "/ceo", icon: Trophy, label: "Kosten & P&L", testId: "nav-ceo", roles: ["master"] },
    { to: "/finance", icon: Banknote, label: t.nav_finance, testId: "nav-finance", roles: ["master", "finanza"] },

    { section: "WERTIA AI", roles: ["master", "control", "basic"] },
    { to: "/compliance", icon: Sparkles, label: "AI Compliance", testId: "nav-compliance", roles: ["master", "control", "basic"] },

    { section: "SYSTEM", roles: ["master"] },
    { to: "/users", icon: UserCog, label: t.users || "Benutzer", testId: "nav-users", roles: ["master"] },
    { to: "/system-health", icon: Activity, label: "System Health", testId: "nav-system-health", roles: ["master"] },
    { to: "/admin/danger-zone", icon: AlertTriangle, label: "Danger Zone", testId: "nav-danger-zone", roles: ["master"] },
  ];
  // 1° filter: keep only items abilitati al ruolo corrente.
  // 2° pass: rimuove le sezioni che non hanno voci a seguire (sezioni "orfane").
  const visible = baseItems.filter((it) => it.roles.includes(role));
  const items = visible.filter((it, i) => {
    if (!it.section) return true;
    for (let j = i + 1; j < visible.length; j++) {
      if (visible[j].section) return false;
      return true;
    }
    return false;
  });

  const pillars = [
    { icon: ShieldCheck, key: "vertrauen", label: t.pillar_trust || "VERTRAUEN" },
    { icon: Award, key: "kompetenz", label: t.pillar_competence || "KOMPETENZ" },
    { icon: Scale, key: "unabhaengigkeit", label: t.pillar_independence || "UNABHÄNGIGKEIT" },
    { icon: Lock, key: "sicherheit", label: t.pillar_security || "SICHERHEIT" },
  ];

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-64"} bg-sidebar flex flex-col fixed lg:relative inset-y-0 left-0 z-40 transform transition-all duration-200 lg:transform-none ${
        open ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0"
      }`}
      data-testid="sidebar"
      data-collapsed={collapsed ? "1" : "0"}
    >
      <button
        type="button"
        onClick={toggleCollapsed}
        className={`hidden lg:flex fixed top-1/2 -translate-y-1/2 z-50 w-1.5 h-32 bg-prime hover:w-2.5 hover:h-40 transition-all cursor-col-resize items-center justify-center group rounded-l shadow-lg shadow-prime/40 ${collapsed ? "left-[58px]" : "left-[250px]"}`}
        aria-label={collapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
        title={collapsed ? "Erweitern" : "Einklappen"}
        data-testid="sidebar-collapse-toggle"
      >
        <span className="opacity-0 group-hover:opacity-100 transition-opacity text-graphite">
          {collapsed
            ? <PanelLeftOpen className="w-3 h-3" strokeWidth={2.5} />
            : <PanelLeftClose className="w-3 h-3" strokeWidth={2.5} />}
        </span>
      </button>

      <div className={`${collapsed ? "px-2 pt-4 pb-3" : "px-6 pt-6 pb-5"} border-b border-white/5 relative transition-all`}>
        <img
          src={LOGO_URL}
          alt="Wertia Finanz"
          className={`mx-auto ${collapsed ? "w-9" : "w-full max-w-[140px]"}`}
        />
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 text-white/60 hover:text-white p-1"
          aria-label="Schliessen"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <nav className={`flex-1 py-4 ${collapsed ? "px-2" : "px-3"} space-y-0.5 overflow-y-auto overflow-x-hidden`}>
        {items.map((it, idx) => {
          if (it.section) {
            if (collapsed) {
              return <div key={`section-${idx}`} className="my-2 h-px bg-white/5" />;
            }
            return (
              <div key={`section-${idx}`} className="px-4 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-white/40">
                {it.section}
              </div>
            );
          }
          const { to, icon: Icon, label, testId, badge, senders } = it;
          return (
            <NavLink
              key={`${to}-${testId}`}
              to={to}
              data-testid={testId}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `relative flex items-center ${collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-4 py-3"} text-sm transition-all rounded-md ${
                  isActive
                    ? "bg-white/5 text-white sidebar-active"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && (
                <span className="flex-1 min-w-0">
                  <span className="truncate block">{label}</span>
                  {senders?.length > 0 && (
                    <span className="block text-[10px] text-prime truncate mt-0.5" data-testid="chat-unread-senders">
                      {senders.slice(0, 3).map((s) => `${(s.name || "").split(" ")[0]} (${s.count})`).join(" · ")}
                      {senders.length > 3 ? " …" : ""}
                    </span>
                  )}
                </span>
              )}
              {badge ? (
                <span
                  className={`${collapsed ? "absolute top-0.5 right-0.5" : "relative"} inline-flex items-center justify-center`}
                  data-testid={`${testId}-badge-wrap`}
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-prime opacity-75 animate-ping" aria-hidden="true" />
                  <span
                    className={`relative inline-flex items-center justify-center text-[10px] font-bold text-white bg-prime rounded-full ${collapsed ? "min-w-[14px] h-[14px] text-[9px]" : "min-w-[18px] h-[18px]"} px-1 shrink-0`}
                    data-testid={`${testId}-badge`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                </span>
              ) : null}
            </NavLink>
          );
        })}
      </nav>

      <div className={`${collapsed ? "px-2" : "px-3"} pb-3 border-t border-white/5 pt-3`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3 px-3"} py-2`}>
          <div
            className={`w-9 h-9 rounded-full ${meta.bg} text-white flex items-center justify-center font-display text-sm shrink-0 ring-2 ring-white/10`}
            data-testid="user-avatar"
            title={collapsed ? `${user?.full_name || ""} · ${meta.label}` : undefined}
          >
            {(user?.full_name || "U").slice(0, 1).toUpperCase()}
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{user?.full_name}</div>
                <div className={`label-uppercase ${meta.text} truncate flex items-center gap-1.5`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.bg}`} />
                  {meta.label}
                </div>
              </div>
              <button
                type="button"
                onClick={toggleTheme}
                className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                title={theme === "dark" ? "Modus chiara" : "Modus scura"}
                aria-label="Toggle theme"
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.7} /> : <Moon className="w-4 h-4" strokeWidth={1.7} />}
              </button>
            </>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="bg-graphite px-3 py-3 border-t border-white/5">
          <div className="grid grid-cols-4 gap-1">
            {pillars.map(({ icon: Icon, key, label }) => (
              <div key={key} className="flex flex-col items-center text-center gap-1" title={label}>
                <Icon className="w-3.5 h-3.5 text-white/60" strokeWidth={1.5} />
                <span className="text-[8px] text-white/40 font-semibold tracking-wider leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
